import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { RouterLink } from '@angular/router';
import { SharedModule } from '@shared/shared.module';
import { PARAMETRIZATION_ROUTE } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/partner-income-tax.constants';
import {
  FindingLevel,
  PartnerIndicatorsDto,
} from '@interfaces/partner-income-tax/responses/partner-income-tax.response';
import {
  describeNetWorthChange,
  formatCompactMoney,
  formatRatio,
  signTone,
} from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/utils/partner-income-tax-format.util';
import { DebtBarComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/kpi-visuals/debt-bar.component';
import { SparklineComponent } from '@features/dashboard/performance/pages/dfp/components/partner-income-tax/components/kpi-visuals/sparkline.component';

/** Os quatro indicadores do sócio: PL, dívidas, capacidade livre de garantia e renda que sobra. */
@Component({
  selector: 'app-pit-partner-indicators',
  standalone: true,
  imports: [SparklineComponent, DebtBarComponent, SharedModule, RouterLink],
  templateUrl: './partner-indicators.component.html',
  styleUrl: './partner-indicators.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerIndicatorsComponent {
  readonly indicators = input.required<PartnerIndicatorsDto>();
  /** Primeiro ano da janela, para "desde 2022". */
  readonly firstYear = input<number | null>(null);
  /**
   * Prévia do teste de renda enquanto o analista digita (recalculada no painel). `undefined` usa o número do
   * backend.
   */
  readonly liveIncomeResult = input<number | null | undefined>(undefined);
  /** Quantos anos há na janela: com 2, a média anual é o próprio "no ano" e não se repete. */
  readonly windowSize = input(0);
  /** Nível do ponto de dívidas sobre o patrimônio, para a cor da nota "% do PL" e a faixa cheia da barra. */
  readonly debtsLevel = input<FindingLevel | null>(null);

  /** PL por ano da janela (valores do backend), para a linha de evolução do KPI. */
  readonly netWorthSeries = input<{ year: number; value: number | null }[]>([]);

  protected readonly parametrizationRoute = PARAMETRIZATION_ROUTE;
  protected readonly money = formatCompactMoney;
  protected readonly ratio = formatRatio;
  protected readonly tone = signTone;

  protected readonly netWorthNote = computed(() => {
    const i = this.indicators();
    return describeNetWorthChange(
      i.netWorthYoY,
      i.netWorthCagr,
      this.firstYear(),
      this.windowSize()
    );
  });

  protected readonly incomeResult = computed(() => {
    const live = this.liveIncomeResult();
    return live !== undefined ? live : this.indicators().incomeTestResult;
  });
}
